import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import axios from "axios";
import heic2any from "heic2any";
import { AnimatePresence, motion, Transition } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { MdClose } from "react-icons/md";
import { Document, Page, pdfjs } from "react-pdf";

import { KsefInvoiceView } from "./KsefInvoiceView";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface FileViewProps {
  url: string;
  fileType: string;
  onClose: () => void;
  ksefNumber?: string | null;
}

const SUPPORTED_TYPES = ["application/pdf", "text/xml", "image/jpeg", "image/png", "image/heic"];

export const FileView: React.FC<FileViewProps> = ({ url, fileType, onClose, ksefNumber }) => {
  const [file, setFile] = useState<Blob | string | Uint8Array | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [containerDimensions, setContainerDimensions] = useState({ width: "100%", height: "100vh" });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  const updateContainerDimensions = (page: any) => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      const viewport = page.getViewport({ scale: 1 });

      let newScale = clientWidth / viewport.width;
      let newHeight = viewport.height * newScale;
      let newWidth = clientWidth;

      if (newHeight > clientHeight) {
        newScale = clientHeight / viewport.height;
        newHeight = clientHeight;
        newWidth = viewport.width * newScale;
      }

      setScale(newScale);
      setContainerDimensions({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      });
    }
  };

  const onDocumentLoadSuccess = (pdf: any) => {
    pdf.getPage(1).then((page: any) => {
      updateContainerDimensions(page);
    });
  };

  useEffect(() => {
    const fetchFile = async () => {
      try {
        setLoading(true);
        if (fileType === "application/pdf" || fileType === "image/heic" || fileType === "text/xml") {
          const response = await axios({
            url,
            responseType:
              fileType === "text/xml" && ksefNumber ? "arraybuffer" : fileType === "text/xml" ? "text" : "blob",
            method: "GET",
          });
          const data = response.data;

          if (fileType === "text/xml") {
            if (ksefNumber && data instanceof ArrayBuffer) {
              setFile(new Uint8Array(data));
            } else {
              setFile(typeof data === "string" ? data : null);
            }
          } else if (fileType === "image/heic") {
            const blob = data;
            try {
              const convertedBlob = await heic2any({ blob, toType: "image/jpeg" });
              const reader = new FileReader();
              reader.readAsDataURL(convertedBlob as Blob);
              reader.onloadend = () => {
                setFile(reader.result as string);
              };
            } catch (conversionError) {
              // eslint-disable-next-line no-console
              console.error("HEIC conversion error:", conversionError);
              setError("Nie udało się załadować podglądu HEIC.");
            }
          } else {
            setFile(data);
          }
        } else {
          setFile(url);
        }
        setError(null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch the file:", err);
        setError("Nie udało się załadować pliku.");
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
  }, [url, fileType, ksefNumber]);

  useEffect(() => {
    const handleResize = () => {
      if (file && fileType === "application/pdf") {
        const loadingTask = pdfjs.getDocument(URL.createObjectURL(file as Blob));
        loadingTask.promise.then((pdf) => {
          pdf.getPage(1).then((page) => {
            updateContainerDimensions(page);
          });
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [file, fileType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const containerVariants = {
    hidden: { opacity: 0, y: -20, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  const containerTransition: Transition = {
    duration: 0.3,
    ease: "easeOut",
  };

  if (!SUPPORTED_TYPES.includes(fileType)) {
    return <div style={{ padding: "16px", color: theme.colors.primary || "#000" }}>Podgląd niedostępny.</div>;
  }

  if (fileType === "text/xml") {
    const showPdfFromXml = ksefNumber && file instanceof Uint8Array;

    return (
      <AnimatePresence>
        <motion.div
          ref={containerRef}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={containerVariants}
          transition={containerTransition}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: showPdfFromXml ? "none" : "900px",
            height: showPdfFromXml ? "100vh" : "90vh",
            position: "relative",
            background: "white",
            borderRadius: theme.smallBorderRadius,
            boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
          }}
        >
          {showPdfFromXml ? (
            <KsefInvoiceView
              ksefNumber={ksefNumber}
              xml={file}
              loading={loading}
              error={error}
              showPreviewFunction={onClose}
              fillContainer
            />
          ) : error ? (
            <div style={{ padding: "16px", color: "red" }}>{error}</div>
          ) : typeof file === "string" ? (
            <pre
              style={{
                flex: 1,
                margin: 0,
                padding: "16px",
                overflow: "auto",
                fontSize: "12px",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {file}
            </pre>
          ) : (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Spinner color={theme.colors.primary} size="28px" />
            </div>
          )}
          {!showPdfFromXml && (
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                background: "white",
                border: "none",
                borderRadius: "50%",
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.25)",
              }}
            >
              <MdClose size={24} color="#000" />
            </button>
          )}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={containerVariants}
        transition={containerTransition}
        style={{
          display: "flex",
          flexDirection: "column",
          width: containerDimensions.width,
          height: containerDimensions.height,
          position: "relative",
          background: "white",
          borderRadius: theme.smallBorderRadius,
          boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
          justifyContent: "center",
          alignItems: "bottom",
        }}
      >
        {fileType === "application/pdf" ? (
          <>
            {error ? (
              <div style={{ padding: "16px", color: "red" }}>{error}</div>
            ) : file ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "bottom",
                  overflow: "hidden",
                }}
              >
                <Document
                  file={file as Blob}
                  loading={
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "180px",
                        width: "100%",
                      }}
                    >
                      <Spinner color={theme.colors.primary} size="28px" />
                    </div>
                  }
                  error={"Nie udało się załadować podglądu."}
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  <Page
                    pageNumber={1}
                    scale={scale}
                    css={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Document>
                <button
                  onClick={onClose}
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    background: "white",
                    border: "none",
                    borderRadius: "50%",
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.25)",
                  }}
                >
                  <MdClose size={24} color="#000" />
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <img
              src={file as string}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "top",
              }}
              alt="File preview"
            />
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                background: "white",
                border: "none",
                borderRadius: "50%",
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.25)",
              }}
            >
              <MdClose size={24} color="#000" />
            </button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
